import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  AdminFormPage,
  useAdminFormPersistence,
} from "@components/CatalogIndex";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import { useV2GetPaymentCreditQuery } from "@generated";
import { PaymentCreditForm } from "../PaymentCreditForm/PaymentCreditForm";
import { usePaymentCreditMutations } from "../api";
import { emptyPaymentCreditForm } from "../types";
import type { PaymentCreditFormValues } from "../types";

const FORM_ID = "payment-credit-form";

export const Edit = (): JSX.Element => {
  const navigate = useNavigate();
  const { id = "" } = useParams();
  const { update } = usePaymentCreditMutations();
  const { data, isLoading, refetch } = useV2GetPaymentCreditQuery(
    { id: Number(id) },
    { refetchOnMountOrArgChange: true }
  );
  const { toast, setToast, savedInitial, formKey, markSaved } =
    useAdminFormPersistence<PaymentCreditFormValues>();
  const [submitting, setSubmitting] = useState(false);

  const loadedInitial = useMemo<PaymentCreditFormValues>(() => {
    const attrs = data?.data?.attributes;
    if (!attrs) return emptyPaymentCreditForm;
    return {
      paymentSource: attrs["payment-source"] ?? "",
      description: attrs.description ?? "",
      criteria: attrs.criteria ?? "",
    };
  }, [data]);

  if (isLoading) return <ScopeStackSpinner />;

  const initialValues = savedInitial ?? loadedInitial;

  const handleSubmit = async (values: PaymentCreditFormValues) => {
    setSubmitting(true);
    try {
      await update(id, values);
      markSaved(values, `'${values.paymentSource}' saved`);
      // The generated API defines no cache tags, so the update can't
      // invalidate the cached GET. Refetch so the cache holds the
      // server-fresh record for the next time Edit is opened.
      refetch();
    } catch (err) {
      console.error("Failed to update payment credit", err);
      setToast({ variant: "danger", text: "Could not save payment credit." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <AdminFormPage
        title="Edit Payment Credit"
        formId={FORM_ID}
        onCancel={() => navigate("../..", { relative: "path" })}
        isSubmitting={submitting}
      >
        <PaymentCreditForm
          key={formKey}
          id={FORM_ID}
          initialValues={initialValues}
          isSubmitting={submitting}
          onSubmit={handleSubmit}
        />
      </AdminFormPage>
      {toast && (
        <ToastAlert
          variant={toast.variant}
          text={toast.text}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
};

export default Edit;
